import {
  ClipboardList,
  Newspaper,
  CircleUser,
  MessagesSquare,
  Info,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '@/components/shared/SEO'
import ClaimUpdatesHero from '@/components/shared/claim-updates/ClaimUpdatesHero'
import UpdateSectionCard from '@/components/shared/claim-updates/UpdateSectionCard'
import WorkChecklist from '@/components/shared/claim-updates/WorkChecklist'
import NewThisMonthCallout from '@/components/shared/claim-updates/NewThisMonthCallout'
import FcaTimeline from '@/components/shared/claim-updates/FcaTimeline'
import QuickLinks from '@/components/shared/claim-updates/QuickLinks'
import UpdateDetailsCta from '@/components/shared/claim-updates/UpdateDetailsCta'
import { claimUpdatesMonthlyContent } from '@/data/claimUpdatesMonthlyContent'

const ClaimUpdatesPage = () => {
  const tribunalHearingDates =
    'either 14–18 December 2026 or 16–26 February 2027'
  const {
    claimUpdate,
    latestIndustryNews,
    whatThisMeansForYou,
    stayingInformed,
    newThisMonth,
  } = claimUpdatesMonthlyContent

  return (
    <div className="flex-1 bg-gray-50">
      <SEO
        title="Car Finance Claim Updates | Your Claim Matters"
        description="Read the latest update about your car finance claim, FCA developments and what they mean for you."
        url="/claim-updates"
        image="/Image/Car-photo-illustration-claims1.webp"
        imageAlt="Car finance claim updates from Your Claim Matters"
        schemaType="WebPage"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Claim Updates', url: '/claim-updates' },
        ]}
      />

      <ClaimUpdatesHero />

      <div className="container mx-auto max-w-6xl px-4 py-8 sm:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px] lg:gap-8">
          <div className="space-y-6">
            <UpdateSectionCard
              icon={ClipboardList}
              heading={claimUpdate.heading}
            >
              {claimUpdate.body.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </UpdateSectionCard>

            <UpdateSectionCard
              icon={Newspaper}
              heading={latestIndustryNews.heading}
            >
              {latestIndustryNews.body.map((paragraph, index) => {
                if (paragraph.includes(tribunalHearingDates)) {
                  const [beforeDates, afterDates] =
                    paragraph.split(tribunalHearingDates)

                  return (
                    <p key={index}>
                      {beforeDates}
                      <strong>{tribunalHearingDates}</strong>
                      {afterDates}
                    </p>
                  )
                }

                return <p key={index}>{paragraph}</p>
              })}
            </UpdateSectionCard>

            <UpdateSectionCard
              icon={CircleUser}
              heading={whatThisMeansForYou.heading}
            >
              {whatThisMeansForYou.body.map((paragraph, index) => (
                <p key={index}>
                  {index === 0 ? <strong>{paragraph}</strong> : paragraph}
                </p>
              ))}
            </UpdateSectionCard>

            <UpdateSectionCard
              icon={MessagesSquare}
              heading="If your lender contacts you"
            >
              <p>
                As your claim progresses, your lender may contact you directly
                while continuing to work with us as your appointed
                representative.
              </p>
              <p>
                If you receive any emails or letters from your lender, simply
                forward them to{' '}
                <a
                  href="mailto:info@yourclaimmatters.co.uk"
                  className="font-semibold text-[#FF914D] underline underline-offset-2"
                >
                  info@yourclaimmatters.co.uk
                </a>
                . We&apos;ll review them, advise you of any action required and
                continue managing the process on your behalf.
              </p>
            </UpdateSectionCard>

            <WorkChecklist />

            <UpdateSectionCard icon={Info} heading={stayingInformed.heading}>
              {stayingInformed.body.map((paragraph, index) => {
                if (paragraph.includes('info@yourclaimmatters.co.uk')) {
                  const [beforeEmail, afterEmail] = paragraph.split(
                    'info@yourclaimmatters.co.uk'
                  )

                  return (
                    <p key={index}>
                      {beforeEmail}
                      <a
                        href="mailto:info@yourclaimmatters.co.uk"
                        className="font-semibold text-[#FF914D] underline underline-offset-2"
                      >
                        info@yourclaimmatters.co.uk
                      </a>
                      {afterEmail}
                    </p>
                  )
                }

                if (paragraph.includes('FAQs')) {
                  const [beforeFaqs, afterFaqs] = paragraph.split('FAQs')

                  return (
                    <p key={index}>
                      {beforeFaqs}
                      <Link
                        to="/faqs"
                        className="font-semibold text-[#FF914D] underline underline-offset-2"
                      >
                        FAQs
                      </Link>
                      {afterFaqs}
                    </p>
                  )
                }

                return <p key={index}>{paragraph}</p>
              })}
            </UpdateSectionCard>

            <UpdateDetailsCta />
          </div>

          <aside className="space-y-6">
            <NewThisMonthCallout
              headline={newThisMonth.headline}
              summary={newThisMonth.summary}
              sourceUrl={newThisMonth.sourceUrl}
            />
            <FcaTimeline />
            <QuickLinks />
          </aside>
        </div>
      </div>
    </div>
  )
}

export default ClaimUpdatesPage
